/**
 * Session Manager
 * Uses skin_id for feature tracking. Creates SkinAnalysis via POST /api/salon/session.
 */

import { salonAppPath, SALON_DETECTION_SESSION_KEY } from '../config/salonApp';
import { SALON_API_PREFIX } from './apiConfig';
import { fetchWithTimeout } from './fetchWithTimeout';
import { fetchWithRetry } from './fetchWithRetry';

export const SESSION_STORAGE_KEY = SALON_DETECTION_SESSION_KEY;
const URL_PARAM_SESSION = 'session_id';

/**
 * Get session_id from URL query (?session_id=xxx or ?sessionId=xxx).
 * @param {{ search?: string } | string} locationOrSearch - useLocation() or location.search
 * @returns {string|null}
 */
export const getSessionIdFromUrl = (locationOrSearch) => {
  const search = typeof locationOrSearch === 'string' ? locationOrSearch : (locationOrSearch?.search || '');
  if (!search) return null;
  const params = new URLSearchParams(search);
  const sid = params.get(URL_PARAM_SESSION) || params.get('sessionId');
  return sid ? String(sid).trim() || null : null;
};

/**
 * Append session_id to path for navigation so it persists in URL (avoids loss when state is cleared).
 * @param {string} path - e.g. '/user-info', '/capture'
 * @param {string|null} sessionId
 * @returns {string} path with ?session_id= or &session_id=
 */
export const pathWithSessionId = (path, sessionId) => {
  const prefixed = salonAppPath(path);
  if (!sessionId || !String(sessionId).trim()) return prefixed;
  const base = prefixed.split('?')[0];
  const existing = prefixed.includes('?') ? prefixed.slice(prefixed.indexOf('?')) : '';
  const params = new URLSearchParams(existing);
  params.set(URL_PARAM_SESSION, String(sessionId).trim());
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

/**
 * Get stored session/skin ID (string - may be skin_id as "123" or legacy UUID)
 * @returns {string|null}
 */
export const getSessionId = () => {
  return localStorage.getItem(SESSION_STORAGE_KEY);
};

/**
 * Get skin_id (number) if we have a numeric session_id
 * @returns {number|null}
 */
export const getSkinId = () => {
  const sid = getSessionId();
  if (!sid || typeof sid !== 'string') return null;
  const n = parseInt(sid, 10);
  return Number.isNaN(n) ? null : n;
};

/**
 * Create a new session (resets current) - calls backend to create SkinAnalysis
 * @param {string} [apiBaseUrl] - If provided, creates via backend; otherwise local only
 * @returns {Promise<string>} session_id (str(skin_id) or UUID)
 */
export const createNewSession = async (apiBaseUrl) => {
  if (apiBaseUrl) {
    return createNewSessionWithBackend(apiBaseUrl);
  }
  const id = createNewSessionLocal();
  return id;
};

/**
 * Create new session without API (local only, for fallback)
 * @returns {string}
 */
export const createNewSessionLocal = () => {
  const newSessionId = generateUUID();
  localStorage.setItem(SESSION_STORAGE_KEY, newSessionId);
  return newSessionId;
};

/**
 * Clear current session
 */
export const clearSession = () => {
  localStorage.removeItem(SESSION_STORAGE_KEY);
};

function generateUUID() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  const timestamp = Date.now().toString(36);
  const randomPart = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
  const performanceId =
    typeof performance !== 'undefined' && performance.now
      ? Math.floor(performance.now() * 1000).toString(36)
      : '';
  return `${timestamp}-${performanceId}-${randomPart}`;
}

function sessionBackendRequiredError(message = 'Could not reach the server to start a session. Check your connection and try again.') {
  const err = new Error(message);
  err.code = 'SESSION_BACKEND_REQUIRED';
  return err;
}

/**
 * Initialize session with backend.
 * Creates new SkinAnalysis (POST) when no session, or validates existing (GET).
 * When app_session_token is provided, new sessions are created with it so the flow stays tied to the same token.
 * @param {string} apiBaseUrl
 * @param {{ app_session_token?: string }} [options] - optional; pass token so new session stays in same token flow
 * @returns {Promise<string>} session_id (skin_id as string for new flow)
 */
export const initializeSession = async (apiBaseUrl, options = {}) => {
  const hasAppToken = Boolean(options.app_session_token && String(options.app_session_token).trim());
  try {
    const stored = getSessionId();
    const baseUrl = (apiBaseUrl || '').replace(/\/$/, '');

    if (!stored) {
      return createNewSessionWithBackend(baseUrl, options);
    }

    const isNumeric = /^\d+$/.test(String(stored).trim());
    let url = isNumeric
      ? `${baseUrl}${SALON_API_PREFIX}/session?skin_id=${stored}`
      : `${baseUrl}${SALON_API_PREFIX}/session?session_id=${encodeURIComponent(stored)}`;

    if (options.app_session_token) {
      url += `&app_session_token=${encodeURIComponent(options.app_session_token)}`;
    }

    const response = await fetchWithTimeout(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      timeoutMs: 20000,
    });

    if (response.ok) {
      const data = await response.json();
      const sid = data.skin_id != null ? String(data.skin_id) : data.session_id;
      if (sid && sid !== stored) {
        localStorage.setItem(SESSION_STORAGE_KEY, sid);
        return sid;
      }
      return stored;
    }

    if (hasAppToken) {
      return createNewSessionWithBackend(baseUrl, options);
    }
    throw sessionBackendRequiredError();
  } catch (error) {
    if (error?.code === 'SESSION_BACKEND_REQUIRED') throw error;
    const stored = getSessionId();
    if (hasAppToken) {
      if (stored) return stored;
      throw sessionBackendRequiredError(error?.message);
    }
    return stored || createNewSessionLocal();
  }
};

/**
 * Create a new skin analysis session via backend.
 * Optionally pass app_session_token (e.g. from landing page URL) and device_info to store at CTA time.
 * @param {string} apiBaseUrl
 * @param {{ app_session_token?: string, device_info?: object, previous_salon_reference?: string, face_user_id?: string, name?: string, mobile_number?: string, email?: string, gender?: string, date_of_birth?: string, age?: number, skin_type?: string }} [options]
 * @returns {Promise<string>} session_id (str(skin_id))
 */
export const createNewSessionWithBackend = async (apiBaseUrl, options = {}) => {
  const hasAppToken = Boolean(options.app_session_token && String(options.app_session_token).trim());
  const baseUrl = (apiBaseUrl || '').replace(/\/$/, '');
  const body = {};
  if (options.app_session_token) {
    body.app_session_token = options.app_session_token;
  }
  if (options.device_info && typeof options.device_info === 'object') {
    body.device_info = options.device_info;
  }
  const prevRef = options.previous_salon_reference;
  if (prevRef && String(prevRef).trim()) {
    body.previous_salon_reference = String(prevRef).trim();
  }
  if (options.face_user_id != null && String(options.face_user_id).trim()) {
    body.face_user_id = String(options.face_user_id).trim();
  }
  if (options.name != null && String(options.name).trim()) {
    body.name = String(options.name).trim();
  }
  if (options.mobile_number != null && String(options.mobile_number).trim()) {
    body.mobile_number = String(options.mobile_number).trim();
  }
  if (options.email != null && String(options.email).trim()) {
    body.email = String(options.email).trim();
  }
  if (options.gender != null && String(options.gender).trim()) {
    body.gender = String(options.gender).trim();
  }
  if (options.date_of_birth != null && String(options.date_of_birth).trim()) {
    body.date_of_birth = String(options.date_of_birth).trim();
  }
  if (options.age != null && options.age !== '') {
    const ageNum = parseInt(options.age, 10);
    if (!Number.isNaN(ageNum)) body.age = ageNum;
  }
  if (options.skin_type != null && String(options.skin_type).trim()) {
    body.skin_type = String(options.skin_type).trim();
  }

  let organizationId = options.organization_id;
  let stylistId = options.stylist_id;
  try {
    const { getFranchiseOrganizationId, getFranchiseStylistId, getFranchiseId } = await import(
      '../../shared/franchiseFlowAuth'
    );
    if (organizationId == null || String(organizationId).trim() === '') {
      organizationId = getFranchiseOrganizationId();
    }
    if (stylistId == null || String(stylistId).trim() === '') {
      stylistId = getFranchiseStylistId();
    }
    if (options.franchise_id == null || String(options.franchise_id).trim() === '') {
      options.franchise_id = getFranchiseId();
    }
  } catch {
    /* optional */
  }
  if (organizationId != null && String(organizationId).trim()) {
    body.organization_id = String(organizationId).trim();
  }
  if (options.franchise_id != null && String(options.franchise_id).trim()) {
    body.franchise_id = String(options.franchise_id).trim();
  }
  if (stylistId != null && String(stylistId).trim() !== '') {
    const sid = parseInt(stylistId, 10);
    if (!Number.isNaN(sid)) body.stylist_id = sid;
  }

  let response;
  try {
    response = await fetchWithRetry(`${baseUrl}${SALON_API_PREFIX}/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: Object.keys(body).length ? JSON.stringify(body) : undefined,
      timeoutMs: 20000,
    }, { label: 'salon-session-create' });
  } catch (err) {
    const stored = getSessionId();
    if (hasAppToken) {
      if (stored) return stored;
      throw sessionBackendRequiredError(err?.message);
    }
    return stored || createNewSessionLocal();
  }

  if (!response.ok) {
    let msg = `Session ${response.status}`;
    try {
      const errData = await response.json();
      if (errData.error) msg = errData.error;
    } catch (_) {}
    throw new Error(msg);
  }

  const data = await response.json();
  const sid = data.skin_id != null ? String(data.skin_id) : data.session_id;
  if (!sid) {
    throw new Error('No session id in response');
  }
  localStorage.setItem(SESSION_STORAGE_KEY, sid);
  return sid;
};

/**
 * End the in-app customer analysis session on the server (releases processing locks).
 * @param {string} [apiBaseUrl]
 * @param {string|null} sessionId
 * @param {string|null} [appSessionToken]
 * @returns {Promise<{ ok: boolean, skipped?: boolean, error?: string }>}
 */
export async function endCustomerSessionOnServer(apiBaseUrl, sessionId, appSessionToken = null) {
  const sid = sessionId != null ? String(sessionId).trim() : '';
  if (!sid) return { ok: true, skipped: true };

  const baseUrl = (apiBaseUrl || '').replace(/\/$/, '');
  if (!baseUrl) return { ok: true, skipped: true };

  try {
    const payload = { session_id: sid, skin_id: sid };
    if (appSessionToken && String(appSessionToken).trim()) {
      payload.app_session_token = String(appSessionToken).trim();
    }
    const response = await fetchWithTimeout(`${baseUrl}${SALON_API_PREFIX}/session/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      timeoutMs: 15000,
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok && data?.success !== false) {
      return { ok: true };
    }
    return { ok: false, error: data?.error || `Session end failed (${response.status})` };
  } catch (e) {
    return { ok: false, error: e?.message || 'Session end request failed' };
  }
}

/**
 * End customer session on server (best-effort), then clear local session id.
 */
export async function finalizeCustomerSession(apiBaseUrl, sessionId, appSessionToken = null) {
  await endCustomerSessionOnServer(apiBaseUrl, sessionId, appSessionToken).catch(() => {});
  clearSession();
}

function buildSessionEndPayload(sessionId, appSessionToken) {
  const sid = String(sessionId || '').trim();
  const payload = { session_id: sid, skin_id: sid };
  if (appSessionToken && String(appSessionToken).trim()) {
    payload.app_session_token = String(appSessionToken).trim();
  }
  return payload;
}

function buildSessionHeaders() {
  return { 'Content-Type': 'application/json', Accept: 'application/json' };
}

/**
 * Best-effort session/end on navigation or tab close (releases server processing locks).
 * Uses fetch keepalive so the request can finish after the page unloads.
 */
export function releaseCustomerSessionKeepalive(apiBaseUrl, sessionId, appSessionToken = null) {
  const sid = sessionId != null ? String(sessionId).trim() : '';
  if (!sid) return false;

  const baseUrl = (apiBaseUrl || '').replace(/\/$/, '');
  if (!baseUrl || typeof fetch !== 'function') return false;

  const url = `${baseUrl}${SALON_API_PREFIX}/session/end`;
  const body = JSON.stringify(buildSessionEndPayload(sid, appSessionToken));
  const headers = buildSessionHeaders();

  try {
    fetch(url, {
      method: 'POST',
      headers,
      body,
      keepalive: true,
    }).catch(() => {});
    return true;
  } catch (_) {
    return false;
  }
}

function buildCancelProcessingPayload(sessionId, ownerRequestId, appSessionToken) {
  const sid = String(sessionId || '').trim();
  const owner = String(ownerRequestId || '').trim();
  const payload = {
    session_id: sid,
    skin_id: sid,
    owner_request_id: owner,
    request_id: owner,
  };
  if (appSessionToken && String(appSessionToken).trim()) {
    payload.app_session_token = String(appSessionToken).trim();
  }
  return payload;
}

/**
 * Cooperatively cancel in-flight detect and release locks owned by that request.
 */
export function cancelCustomerProcessingKeepalive(
  apiBaseUrl,
  sessionId,
  ownerRequestId,
  appSessionToken = null
) {
  const sid = sessionId != null ? String(sessionId).trim() : '';
  const owner = ownerRequestId != null ? String(ownerRequestId).trim() : '';
  if (!sid || !owner) return false;

  const baseUrl = (apiBaseUrl || '').replace(/\/$/, '');
  if (!baseUrl || typeof fetch !== 'function') return false;

  const url = `${baseUrl}${SALON_API_PREFIX}/session/cancel-processing`;
  const body = JSON.stringify(buildCancelProcessingPayload(sid, owner, appSessionToken));
  const headers = {
    ...buildSessionHeaders(),
    'X-Request-ID': owner,
  };

  try {
    fetch(url, {
      method: 'POST',
      headers,
      body,
      keepalive: true,
    }).catch(() => {});
    return true;
  } catch (_) {
    return false;
  }
}
